import * as React from "react";
import { omit } from "ramda";
import { Selection, type IObjectWithKey } from "@fluentui/react";
import { action, computed, makeObservable } from "mobx";
import { createUuid, type UUID } from "../../utils/uuid";
import type { ShiftSortKey, Shift } from "../../api";
import { observer } from "mobx-react";
import type { SortState } from "../../utils/sort-state";
import type { PaginationState } from "../../utils/pagination-state";
import { LayoutList } from "../layouts/layout-list";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import type { SelectionState } from "../../utils/selection-state";
import {
    type ShiftsApiResource,
    type ShiftsQuery,
} from "../../domain/repositories/repository-shifts";
import { defaultPageSize } from "../../utils/constants";

export interface ListShiftsProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
}

interface ListShiftItem extends IObjectWithKey {
    readonly label: string;
    readonly createdAt: string;
    readonly key: UUID;
}

import { store } from "../../store";

@observer
export class ListShifts extends React.Component<ListShiftsProps> {
    private _listStateId = createUuid();
    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    constructor(props: ListShiftsProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.shifts.initializeList(this.listStateId, {
            query: () => this.query,
            // Ignore this the url history for this list, as this is always shown in conjunction with other lists.
            ignoreUrl: true,
        });
    }
    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<ShiftSortKey> {
        return store.serviceListStates.shifts.getSortState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<ShiftsApiResource> {
        return store.serviceListStates.shifts.getPaginationState(this.listStateId);
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.shifts.getSelectionState(this.listStateId);
    }

    @computed private get query(): ShiftsQuery {
        return { pageSize: defaultPageSize, ...this.sortState.query };
    }

    @computed private get shifts(): (Shift | undefined)[] {
        if (this.props.ids) {
            return this.props.ids.map((id) => store.repositoryShifts.byId(id));
        }
        return store.repositoryShifts.byQuery(this.query, this.paginationState.pagination);
    }

    @computed private get items(): (ListShiftItem | undefined)[] {
        return this.shifts.map((shift) => {
            if (!shift) {
                return;
            }
            return {
                label: shift.label,
                createdAt: store.i18n.formatDateOnly(shift.createdAt),
                key: shift.id,
            };
        });
    }

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.shifts.isInitialized(this.listStateId)) {
            return <></>;
        }
        // Forward properties that are used by Fluent UI to the `ShimmeredDetailsList`.
        const props = omit(["ids"], this.props);
        return (
            <LayoutList paginationState={this.paginationState} commandBar={this.props.commandBar}>
                <ElofleetList
                    {...props}
                    selection={this.selection}
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    columns={this.sortState.patchColumns([
                        {
                            fieldName: "label",
                            name: store.i18n.t("listShifts.column.label.name"),
                            key: "label",
                            minWidth: 100,
                        },
                        {
                            fieldName: "createdAt",
                            name: store.i18n.t("listShifts.column.createdAt.name"),
                            key: "createdAt",
                            minWidth: 100,
                        },
                    ])}
                    items={this.items}
                />
            </LayoutList>
        );
    }
}
